import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Trash2, X, Send, Download, FileText, Sparkles, Expand, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtMoney(v, cur = "EUR") {
    if (v == null) return "—";
    try {
        return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }).format(v);
    } catch {
        return `${v} ${cur}`;
    }
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return iso;
    }
}

function EmailModal({ open, onClose, onSend }) {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await onSend(email, message);
            onClose();
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="email-modal">
            <div
                className="absolute inset-0 backdrop-blur-2xl bg-black/20"
                onClick={onClose}
            />
            <div className="relative bg-white rounded-3xl tk-shadow-float w-full max-w-md p-8 tk-rise">
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 w-9 h-9 rounded-full bg-[#F5F5F7] hover:bg-[#E5E5EA] flex items-center justify-center"
                    data-testid="email-modal-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Envoyer par email</h2>
                <p className="mt-2 text-sm text-[#86868B]">Le ticket sera envoyé avec sa version numérisée.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Destinataire</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="destinataire@email.com"
                            className="tk-input"
                            data-testid="email-modal-recipient"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Message (optionnel)</label>
                        <textarea
                            rows={3}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            placeholder="Voici le ticket comme demandé…"
                            className="tk-input"
                            data-testid="email-modal-message"
                        />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="tk-btn tk-btn-secondary flex-1"
                            data-testid="email-modal-cancel"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={sending}
                            className="tk-btn tk-btn-blue flex-1"
                            data-testid="email-modal-send"
                        >
                            <Send className="w-4 h-4" />
                            {sending ? "Envoi…" : "Envoyer"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EditModal({ open, receipt, onClose, onSaved }) {
    const [form, setForm] = useState({ merchant: "", total_amount: "", vat: "", purchase_date: "", category: "" });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && receipt) {
            setForm({
                merchant: receipt.merchant || "",
                total_amount: receipt.total_amount ?? "",
                vat: receipt.vat ?? "",
                purchase_date: receipt.purchase_date ? String(receipt.purchase_date).slice(0, 10) : "",
                category: receipt.category || "",
            });
        }
    }, [open, receipt]);

    if (!open) return null;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {};
            if (form.merchant.trim()) payload.merchant = form.merchant.trim();
            if (form.total_amount !== "") payload.total_amount = parseFloat(String(form.total_amount).replace(",", "."));
            if (form.vat !== "") payload.vat = parseFloat(String(form.vat).replace(",", "."));
            if (form.purchase_date) payload.purchase_date = form.purchase_date;
            if (form.category.trim()) payload.category = form.category.trim().toLowerCase();
            const { data } = await api.patch(`/receipts/${receipt.receipt_id}`, payload);
            toast.success("Justificatif mis à jour");
            onSaved(data);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Échec de la mise à jour");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-receipt-modal">
            <div className="absolute inset-0 backdrop-blur-2xl bg-black/20" onClick={onClose} />
            <div className="relative bg-white rounded-3xl tk-shadow-float w-full max-w-md p-8 tk-rise max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 w-9 h-9 rounded-full bg-[#F5F5F7] hover:bg-[#E5E5EA] flex items-center justify-center"
                    data-testid="edit-receipt-close"
                >
                    <X className="w-4 h-4" />
                </button>
                <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Corriger le justificatif</h2>
                <p className="mt-2 text-sm text-[#86868B]">
                    L&apos;IA peut se tromper — ajustez les informations extraites si besoin.
                </p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Marchand</label>
                        <input
                            type="text"
                            value={form.merchant}
                            onChange={set("merchant")}
                            placeholder="Nom du marchand"
                            className="tk-input"
                            data-testid="edit-receipt-merchant"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Montant TTC (€)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.total_amount}
                                onChange={set("total_amount")}
                                placeholder="0,00"
                                className="tk-input"
                                data-testid="edit-receipt-amount"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#1D1D1F] mb-2">TVA (€)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.vat}
                                onChange={set("vat")}
                                placeholder="0,00"
                                className="tk-input"
                                data-testid="edit-receipt-vat"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Date d&apos;achat</label>
                        <input
                            type="date"
                            value={form.purchase_date}
                            onChange={set("purchase_date")}
                            className="tk-input"
                            data-testid="edit-receipt-date"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-[#1D1D1F] mb-2">Catégorie</label>
                        <input
                            type="text"
                            value={form.category}
                            onChange={set("category")}
                            placeholder="restaurant, transport, logiciel…"
                            className="tk-input"
                            data-testid="edit-receipt-category"
                        />
                    </div>
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="tk-btn tk-btn-secondary flex-1"
                            data-testid="edit-receipt-cancel"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="tk-btn tk-btn-blue flex-1"
                            data-testid="edit-receipt-save"
                        >
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function ReceiptDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [receipt, setReceipt] = useState(null);
    const [showEmail, setShowEmail] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

    useEffect(() => {
        api.get(`/receipts/${id}`).then((r) => setReceipt(r.data)).catch(() => navigate("/app/transactions"));
        // `api`, `setReceipt` are stable references; only `id` and `navigate` drive re-runs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, navigate]);

    // When a receipt has no source image (Gmail receipts often don't), fetch
    // the generated Ticketify PDF (thermal ticket / A4 invoice) and surface it
    // as a live preview iframe instead of the bland "Pas d'image" placeholder.
    useEffect(() => {
        if (!receipt) return;
        if (receipt.image_base64) return;
        let revoked = false;
        let url = null;
        (async () => {
            try {
                const res = await api.get(`/receipts/${id}/pdf`, { responseType: "blob" });
                if (revoked) return;
                url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                setPdfPreviewUrl(url);
            } catch (err) {
                // PDF preview is non-blocking: show data + actions even if
                // the inline preview can't load (private mode, slow network…).
                // We surface a warning so prod errors can be traced via Sentry
                // / browser console without breaking the page.
                if (process.env.NODE_ENV !== "production") {
                    console.warn("Receipt PDF preview load failed:", err);
                }
            }
        })();
        return () => {
            revoked = true;
            if (url) window.URL.revokeObjectURL(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [receipt?.receipt_id, receipt?.image_base64]);

    const handleSendEmail = async (email, message) => {
        const tid = toast.loading("Envoi en cours…");
        try {
            await api.post(`/receipts/${id}/send-email`, { recipient_email: email, message });
            toast.success(`Ticket envoyé à ${email}`, { id: tid });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Échec de l'envoi", { id: tid });
            throw err;
        }
    };

    const handleDelete = async () => {
        if (!confirm("Supprimer ce ticket ?")) return;
        await api.delete(`/receipts/${id}`);
        toast.success("Ticket supprimé");
        navigate("/app/transactions");
    };

    const handleDeleteOriginalPdf = async () => {
        if (!confirm("Retirer le PDF joint par le marchand ?\n\nL'aperçu et le téléchargement utiliseront à la place le justificatif généré par Ticketify (ticket de caisse ou facture A4).")) return;
        try {
            await api.delete(`/receipts/${id}/original-pdf`);
            toast.success("PDF original retiré — fallback sur le justificatif Ticketify");
            // Refresh
            const { data } = await api.get(`/receipts/${id}`);
            setReceipt(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec");
        }
    };

    const handleDownloadPdf = async () => {
        const tid = toast.loading("Génération du PDF…");
        try {
            const response = await api.get(`/receipts/${id}/pdf`, { responseType: "blob" });
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const merchant = (receipt.merchant || "ticket").toLowerCase().replace(/\s+/g, "-");
            const a = document.createElement("a");
            a.href = url;
            a.download = `justificatif-${merchant}-${id.slice(-6)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF téléchargé", { id: tid });
        } catch {
            toast.error("Échec du téléchargement", { id: tid });
        }
    };

    if (!receipt) {
        return <p className="text-center text-[#86868B] py-12">Chargement…</p>;
    }

    return (
        <div className="max-w-5xl mx-auto px-6 sm:px-8 space-y-8">
            <button
                onClick={() => navigate(-1)}
                className="tk-btn tk-btn-secondary"
                data-testid="receipt-back-btn"
            >
                <ArrowLeft className="w-4 h-4" /> Retour
            </button>

            <div className="grid lg:grid-cols-5 gap-8">
                {/* Image preview */}
                <div className="lg:col-span-2 tk-rise">
                    <div className="tk-card p-4 sticky top-28">
                        {receipt.image_base64 ? (
                            <button
                                type="button"
                                onClick={() => setZoomOpen(true)}
                                className="group relative block w-full overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF]"
                                aria-label="Agrandir la facture"
                                data-testid="receipt-image-zoom-btn"
                            >
                                <img
                                    src={`data:${receipt.image_mime || "image/jpeg"};base64,${receipt.image_base64}`}
                                    alt="Aperçu de la facture"
                                    className="w-full rounded-2xl transition-transform duration-500 group-hover:scale-[1.02]"
                                    data-testid="receipt-image"
                                />
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                                    <span className="opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 backdrop-blur text-sm font-semibold text-[#1D1D1F] tk-shadow-float">
                                        <Expand className="w-4 h-4" /> Agrandir
                                    </span>
                                </div>
                                {/* Top-right corner badge — always visible */}
                                <span className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center tk-shadow-float text-[#1D1D1F] opacity-90 group-hover:opacity-0 transition-opacity duration-300">
                                    <Expand className="w-4 h-4" />
                                </span>
                            </button>
                        ) : pdfPreviewUrl ? (
                            <button
                                type="button"
                                onClick={() => setZoomOpen(true)}
                                className="group relative block w-full overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF]"
                                aria-label="Agrandir le justificatif"
                                data-testid="receipt-pdf-zoom-btn"
                            >
                                {/* Static "document card" — NOT a live iframe.
                                   Shows a stylized PDF representation; clicking opens the real PDF in the lightbox. */}
                                <div className="relative w-full aspect-[3/4] bg-gradient-to-br from-white to-[#F5F5F7] rounded-2xl overflow-hidden tk-shadow-elev border border-black/5 transition-all duration-500 group-hover:tk-shadow-float group-hover:-translate-y-0.5">
                                    {/* Decorative paper lines */}
                                    <div className="absolute inset-x-8 top-12 space-y-2.5" aria-hidden>
                                        <div className="h-2 w-3/4 rounded-full bg-[#1D1D1F]/8" />
                                        <div className="h-2 w-1/2 rounded-full bg-[#1D1D1F]/6" />
                                    </div>
                                    <div className="absolute inset-x-8 top-32 space-y-2" aria-hidden>
                                        <div className="h-1.5 w-full rounded-full bg-[#1D1D1F]/5" />
                                        <div className="h-1.5 w-11/12 rounded-full bg-[#1D1D1F]/5" />
                                        <div className="h-1.5 w-10/12 rounded-full bg-[#1D1D1F]/5" />
                                        <div className="h-1.5 w-9/12 rounded-full bg-[#1D1D1F]/5" />
                                    </div>
                                    <div className="absolute inset-x-8 bottom-16 space-y-1.5" aria-hidden>
                                        <div className="h-1 w-2/3 rounded-full bg-[#1D1D1F]/8" />
                                        <div className="h-3 w-1/3 rounded-full bg-[#1D1D1F]/10" />
                                    </div>

                                    {/* Centered icon + label */}
                                    <div className="relative h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#007AFF] to-[#0A84FF] flex items-center justify-center tk-shadow-float text-white transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                                            <FileText className="w-7 h-7" strokeWidth={2} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-[#1D1D1F]">Justificatif Ticketify</p>
                                            <p className="text-xs text-[#86868B] mt-0.5">PDF · cliquez pour agrandir</p>
                                        </div>
                                    </div>

                                    {/* Hover overlay */}
                                    <div className="absolute inset-0 bg-[#007AFF]/0 group-hover:bg-[#007AFF]/5 transition-colors duration-300 flex items-center justify-center">
                                        <span className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white tk-shadow-float text-sm font-semibold text-[#1D1D1F]">
                                            <Expand className="w-4 h-4" /> Agrandir
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ) : (
                            <div className="aspect-[3/4] flex flex-col items-center justify-center bg-[#F5F5F7] rounded-2xl text-[#86868B] gap-2" data-testid="receipt-preview-loading">
                                <FileText className="w-8 h-8 opacity-40" />
                                <p className="text-xs">Génération de l&apos;aperçu…</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Details */}
                <div className="lg:col-span-3 tk-rise tk-rise-1 space-y-6">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">{receipt.category || "autre"}</p>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                            {receipt.merchant || "Ticket"}
                        </h1>
                        <p className="mt-4 text-5xl font-semibold tracking-tight text-[#1D1D1F]">
                            {fmtMoney(receipt.total_amount, receipt.currency)}
                        </p>
                    </div>

                    <div className="tk-card overflow-hidden">
                        {[
                            ["Date d'achat", fmtDate(receipt.purchase_date)],
                            ["Catégorie", <span className="capitalize">{receipt.category}</span>],
                            ["TVA", receipt.vat != null ? fmtMoney(receipt.vat, receipt.currency) : "—"],
                            ["Devise", receipt.currency || "EUR"],
                            ["Numérisé le", fmtDate(receipt.created_at)],
                        ].map(([k, v], i) => (
                            <div
                                key={k}
                                className={`flex justify-between items-center px-6 py-4 ${i > 0 ? "border-t border-black/5" : ""}`}
                            >
                                <span className="text-sm text-[#86868B]">{k}</span>
                                <span className="text-sm font-medium text-[#1D1D1F]">{v}</span>
                            </div>
                        ))}
                    </div>

                    {receipt.items?.length > 0 && (
                        <div className="tk-card p-6">
                            <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#86868B] mb-4">Articles</p>
                            <div className="divide-y divide-black/5">
                                {receipt.items.map((it, i) => (
                                    <div key={`${it.name || "item"}-${i}-${it.price || ""}`} className="flex justify-between py-3 text-sm">
                                        <span className="text-[#1D1D1F]">{it.name || `Article ${i + 1}`}</span>
                                        <span className="text-[#86868B]">
                                            {it.qty ? `${it.qty} × ` : ""}{it.price || ""}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={handleDownloadPdf}
                            className="tk-btn tk-btn-blue"
                            data-testid="download-pdf-btn"
                        >
                            <Download className="w-4 h-4" />
                            {receipt.has_original_pdf ? "Télécharger la facture" : "Télécharger PDF"}
                        </button>
                        <button
                            onClick={() => setShowEdit(true)}
                            className="tk-btn tk-btn-secondary"
                            data-testid="edit-receipt-btn"
                        >
                            <Pencil className="w-4 h-4" /> Modifier
                        </button>
                        <button
                            onClick={() => setShowEmail(true)}
                            className="tk-btn tk-btn-secondary"
                            data-testid="send-email-btn"
                        >
                            <Mail className="w-4 h-4" /> Envoyer par email
                        </button>
                        <button
                            onClick={handleDelete}
                            className="tk-btn tk-btn-secondary"
                            data-testid="delete-receipt-btn"
                        >
                            <Trash2 className="w-4 h-4" /> Supprimer
                        </button>
                    </div>

                    {receipt.has_original_pdf ? (
                        (() => {
                            const score = receipt.original_pdf_confidence_score;
                            // Tier the visual badge based on the filename score
                            // assigned by the backend `_filename_score` helper.
                            //   >= 90  → strong (Facture / Invoice / Receipt)
                            //   50..89 → likely (order summary, payment confirmation)
                            //   <  50  → uncertain (generic filename) — user
                            //            should double-check.
                            let tier = "strong";
                            if (typeof score === "number") {
                                if (score < 50) tier = "uncertain";
                                else if (score < 90) tier = "likely";
                            }
                            const tone =
                                tier === "uncertain"
                                    ? { bg: "bg-[#FF9F0A]/8", border: "border-[#FF9F0A]/30", text: "text-[#B26A00]", label: "Document inhabituel", desc: "Vérifie qu'il s'agit bien de ta facture — le nom de fichier ne contient pas les mots habituels (facture, invoice, reçu…)." }
                                    : tier === "likely"
                                    ? { bg: "bg-[#34C759]/6", border: "border-[#34C759]/20", text: "text-[#1D7A33]", label: "Document probable", desc: "Probablement la bonne facture (récap de commande ou confirmation de paiement)." }
                                    : { bg: "bg-[#34C759]/8", border: "border-[#34C759]/20", text: "text-[#1D7A33]", label: "Facture détectée", desc: "Le nom de fichier correspond clairement à une facture." };
                            return (
                                <div className={`flex items-start gap-3 p-4 rounded-2xl ${tone.bg} border ${tone.border}`} data-testid="original-pdf-card">
                                    <FileText className={`w-4 h-4 mt-0.5 shrink-0 ${tone.text}`} />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-[#1D1D1F] leading-relaxed">
                                            <strong>Facture originale</strong> fournie par le marchand ({receipt.original_pdf_filename || "PDF"}). Téléchargement direct, sans regénération — c&apos;est le document officiel.
                                        </p>
                                        <p className={`mt-1.5 text-[11px] font-medium ${tone.text}`} data-testid="pdf-confidence-badge">
                                            {tier === "uncertain" ? "⚠ " : "✓ "}
                                            {tone.label}
                                            {typeof score === "number" && (
                                                <span className="text-[#86868B] font-normal"> · score {score}</span>
                                            )}
                                        </p>
                                        <p className="mt-0.5 text-[11px] text-[#86868B] leading-snug">{tone.desc}</p>
                                        <button
                                            onClick={handleDeleteOriginalPdf}
                                            className="mt-2 text-[11px] text-[#86868B] underline hover:text-[#FF3B30] transition"
                                            data-testid="remove-original-pdf-btn"
                                        >
                                            Mauvais document ? Retirer ce PDF et utiliser le justificatif Ticketify
                                        </button>
                                    </div>
                                </div>
                            );
                        })()
                    ) : (
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-[#F5F5F7] border border-black/5">
                            <Sparkles className="w-4 h-4 text-[#86868B] mt-0.5 shrink-0" />
                            <p className="text-xs text-[#86868B] leading-relaxed">
                                Justificatif généré par Ticketify à partir des informations extraites de l&apos;e-mail. Format ticket de caisse ou facture A4 selon la richesse des données.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <EmailModal open={showEmail} onClose={() => setShowEmail(false)} onSend={handleSendEmail} />

            <EditModal
                open={showEdit}
                receipt={receipt}
                onClose={() => setShowEdit(false)}
                onSaved={(updated) => setReceipt(updated)}
            />

            <ImageLightbox
                open={zoomOpen}
                src={receipt.image_base64 ? `data:${receipt.image_mime || "image/jpeg"};base64,${receipt.image_base64}` : null}
                pdfSrc={!receipt.image_base64 ? pdfPreviewUrl : null}
                title={receipt.merchant}
                onClose={() => setZoomOpen(false)}
                onDownload={handleDownloadPdf}
                downloadLabel={receipt.has_original_pdf ? "Télécharger la facture" : "Télécharger PDF"}
            />
        </div>
    );
}

function ImageLightbox({ open, src, pdfSrc, title, onClose, onDownload, downloadLabel }) {
    useEffect(() => {
        if (!open) return;
        const esc = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", esc);
        // Lock body scroll while open
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", esc);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open || (!src && !pdfSrc)) return null;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 tk-fade-in"
            role="dialog"
            aria-modal="true"
            aria-label="Aperçu agrandi de la facture"
            data-testid="receipt-lightbox"
        >
            <button
                onClick={onClose}
                aria-label="Fermer l'aperçu"
                className="absolute inset-0 bg-black/85 backdrop-blur-md cursor-zoom-out"
                data-testid="receipt-lightbox-backdrop"
            />

            {/* Top action bar */}
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-4 z-10">
                <p className="text-sm font-medium text-white/90 truncate max-w-[60%]">
                    {title || "Facture"}
                </p>
                <div className="flex items-center gap-2">
                    <button
                        onClick={onDownload}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 hover:bg-white text-sm font-semibold text-[#1D1D1F] transition tk-shadow-float"
                        data-testid="receipt-lightbox-download"
                    >
                        <Download className="w-4 h-4" /> {downloadLabel}
                    </button>
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white transition flex items-center justify-center backdrop-blur"
                        data-testid="receipt-lightbox-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Image or PDF preview */}
            {src ? (
                <img
                    src={src}
                    alt="Facture agrandie"
                    className="relative max-h-[90vh] max-w-[92vw] w-auto h-auto object-contain rounded-2xl tk-shadow-float tk-rise"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="receipt-lightbox-image"
                />
            ) : (
                <iframe
                    src={`${pdfSrc}#view=FitH`}
                    title="Justificatif PDF agrandi"
                    className="relative w-[92vw] h-[90vh] max-w-4xl bg-white rounded-2xl tk-shadow-float tk-rise"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="receipt-lightbox-pdf"
                />
            )}

            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60 select-none">
                Cliquez en dehors ou appuyez sur <kbd className="px-1.5 py-0.5 rounded bg-white/15 font-mono text-[10px]">Esc</kbd> pour fermer
            </p>
        </div>
    );
}
